import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { personSlice } from "../../../redux";
import Button from "../../../components/staff/Button";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import Modal from "../../../components/Atomos/Modals/Modal";
import ManageDataAdmin from "./ManageDataAdmin";
import { formatDateLongString } from "../../../utils/dates";
import { IOfficeNew } from "../../../interfaces/models/IOffice";
import type { DataAdminView } from "../../../interfaces/models/IDataAdmin";
import type { FirmContactView } from "../../../interfaces/models/IFirmContact";
import {
  ensurePrimaryOfficeDataAdminRow,
  getPrimaryOfficeId,
  mergeDataAdminBlocksByTier,
  formatOfficeTabLabel,
  type DataAdminOfficeRow,
} from "./manageDataAdminOfficeRules";
import {
  ResponsiveProfileRecords,
  type ProfileRecordItem,
} from "./ResponsiveProfileRecords";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import ProfileOfficeTabs, {
  useProfileOfficeTabSelection,
} from "./ProfileOfficeTabs";

export const PERSON_ROLE_DATA_ADMIN = "dataAdmin" as const;
export const PERSON_ROLE_MEMBER = "member" as const;

export function isPersonRoleDataAdmin(role: unknown): boolean {
  return String(role ?? "") === PERSON_ROLE_DATA_ADMIN;
}

/** @deprecated Use DataAdminView from Cosmos DataAdmin table. */
export interface IDataAdminProfile {
  id: string;
  name: string;
  firmEmail: string;
  lastLogin: string | number;
  role?: string;
  generatedId?: number;
}

export function mapLastLoginRowToDataAdminProfile(
  row: Record<string, unknown>,
): IDataAdminProfile {
  const gid = row.generatedId;
  return {
    id: String(row.id ?? ""),
    name: String(row.name ?? ""),
    firmEmail: String(row.firmEmail ?? ""),
    lastLogin: (row.lastLogin as string | number) ?? "",
    role: String(row.role ?? ""),
    generatedId:
      typeof gid === "number" ? gid : gid != null ? Number(gid) : undefined,
  };
}

export interface DataAdminProfileProps {
  dataAdminData: DataAdminView[];
  offices: IOfficeNew[];
  membershipType?: string;
  primaryContact?: FirmContactView["primaryContact"];
  readOnly?: boolean;
  profileAttorneyPath?: (generatedId: string | number) => string;
  onDataAdminsChange?: (data: DataAdminView[]) => void;
}

function toMobileRecords(
  rows: DataAdminOfficeRow[],
  renderName: (row: DataAdminOfficeRow) => ReactNode,
): ProfileRecordItem[] {
  return rows.map((row) => ({
    key: row.rowKey,
    fields: [
      { label: "Name", value: renderName(row) },
      { label: "Email", value: row.firmEmail || "-" },
      {
        label: "Last Login",
        value: row.lastLogin
          ? formatDateLongString(Number(row.lastLogin))
          : "-",
      },
    ],
  }));
}

const DataAdminProfile: FC<DataAdminProfileProps> = ({
  dataAdminData,
  offices,
  membershipType,
  primaryContact,
  readOnly = false,
  profileAttorneyPath,
  onDataAdminsChange,
}) => {
  const dispatch = useDispatch();
  const [showEditDataAdminModal, setShowEditDataAdminModal] = useState(false);
  const isStaffMode = useStaffMode();

  const displayBlocks = useMemo(() => {
    const primaryOfficeId = getPrimaryOfficeId(offices);
    return mergeDataAdminBlocksByTier(dataAdminData, offices).map((block) => {
      const officeId = String(block.office.id ?? "").trim();
      if (!primaryOfficeId || officeId !== primaryOfficeId) {
        return block;
      }
      return {
        ...block,
        rows: ensurePrimaryOfficeDataAdminRow(block.rows, {
          offices,
          membershipType,
          primaryContact,
        }),
      };
    });
  }, [dataAdminData, offices, membershipType, primaryContact]);

  const officeTabs = useMemo(
    () =>
      displayBlocks.map(({ office }) => ({
        id: String(office.id ?? "").trim(),
        label: formatOfficeTabLabel(office),
      })),
    [displayBlocks],
  );

  const { activeTabId: activeOfficeId, setActiveTabId: setActiveOfficeId } =
    useProfileOfficeTabSelection(officeTabs);

  const activeBlock = useMemo(
    () =>
      displayBlocks.find(
        (block) => String(block.office.id ?? "").trim() === activeOfficeId,
      ),
    [displayBlocks, activeOfficeId],
  );

  const renderName = (row: DataAdminOfficeRow): ReactNode => {
    const profileId = row.legacyId ?? row.generatedId;
    if (profileId != null && String(profileId).trim() !== "") {
      const profilePath = profileAttorneyPath
        ? profileAttorneyPath(profileId)
        : `/staff/profile/attorney/${profileId}`;
      return (
        <Link
          onClick={() =>
            dispatch(
              personSlice.actions.ModifyPerson({
                id: row.personId,
                name: row.name,
                firmEmail: row.firmEmail,
                generatedId: row.generatedId,
              }),
            )
          }
          to={profilePath}
        >
          {row.name || row.personId}
        </Link>
      );
    }
    return row.name || row.personId;
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-gray-100">
      <div className="flex flex-col gap-4 px-4 py-2">
        {!readOnly && (
          <>
            <Modal
              isModalOpen={showEditDataAdminModal}
              toggleModal={() => setShowEditDataAdminModal(false)}
              title="Manage Data Admins"
              className="max-w-[1000px]"
              bodyClassName="min-h-0 overflow-visible"
            >
              <ManageDataAdmin
                dataAdminData={dataAdminData}
                offices={offices}
                membershipType={membershipType}
                primaryContact={primaryContact}
                onClose={() => setShowEditDataAdminModal(false)}
                onSaved={(updatedData) => {
                  onDataAdminsChange?.(updatedData);
                  setShowEditDataAdminModal(false);
                }}
              />
            </Modal>
            <div className="flex items-center gap-2">
              <h3
                className={`w-full ${isStaffMode ? "text-secondary" : "text-primary"}`}
              >
                Data Admin Profile
              </h3>
              <div className="flex items-end justify-end">
                <Button
                  text="Edit Data Admin(s)"
                  color={isStaffMode ? "secondary" : "primary"}
                  variant="filled"
                  size="fixed"
                  onClick={() => setShowEditDataAdminModal(true)}
                  icon={<Plus />}
                  labelNonVisible={["xxs", "xs"]}
                />
              </div>
            </div>
          </>
        )}
        <ProfileOfficeTabs
          tabs={officeTabs}
          activeTabId={activeOfficeId}
          onSelect={setActiveOfficeId}
          className="mb-1 -mx-4"
        />
        {activeBlock ? (
          <div className="gap-2 text-gray-font">
            {activeBlock.rows.length === 0 ? (
              <div className="text-sm text-gray-300 italic pt-2">
                No data admins assigned
              </div>
            ) : (
              <>
                <ResponsiveProfileRecords
                  records={toMobileRecords(activeBlock.rows, renderName)}
                />
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="py-2 text-left font-semibold">Name</th>
                        <th className="py-2 text-left font-semibold">Email</th>
                        <th className="py-2 text-left font-semibold">
                          Last Login
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeBlock.rows.map((row: DataAdminOfficeRow) => (
                        <tr
                          key={row.rowKey}
                          className="border-b border-gray-50 last:border-0"
                        >
                          <td className="py-1 align-top">{renderName(row)}</td>
                          <td className="py-1 align-top break-all">
                            {row.firmEmail || "-"}
                          </td>
                          <td className="py-1 align-top">
                            {row.lastLogin
                              ? formatDateLongString(Number(row.lastLogin))
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DataAdminProfile;
